import { useEffect, useState, useCallback } from 'react';
import { Download, Loader2, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import {
  OversightStats,
  OversightFilters,
  ConversationTable,
  ReviewPanel,
} from '../components/oversight';
import type {
  OversightStats as OversightStatsType,
  OversightConversation,
  OversightConversationDetail,
  OversightPagination,
  OversightFilters as OversightFiltersType,
  ChatCategory,
  UpdateReviewRequest,
} from '../types';
import { PageHeader } from '../../ui';

interface Site {
  id: string;
  name: string;
}

interface Protocol {
  id: string;
  protocol_number: string;
}

export function Oversight() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Check super admin access
  useEffect(() => {
    if (user && user.role !== 'super_admin') {
      navigate('/intelligence/chat');
    }
  }, [user, navigate]);

  // State
  const [stats, setStats] = useState<OversightStatsType | null>(null);
  const [conversations, setConversations] = useState<OversightConversation[]>([]);
  const [pagination, setPagination] = useState<OversightPagination>({
    page: 1,
    per_page: 20,
    total: 0,
    total_pages: 0,
  });
  const [filters, setFilters] = useState<OversightFiltersType>({
    page: 1,
    per_page: 20,
  });
  const [categories, setCategories] = useState<ChatCategory[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [protocols, setProtocols] = useState<Protocol[]>([]);

  // Loading states
  const [statsLoading, setStatsLoading] = useState(true);
  const [conversationsLoading, setConversationsLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  // Review panel state
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [selectedConversation, setSelectedConversation] = useState<OversightConversationDetail | null>(null);
  const [panelLoading, setPanelLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load stats
  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const response = await api.get('/api/intelligence/oversight/stats', {
        params: {
          site_id: filters.site_id,
          date_from: filters.date_from,
          date_to: filters.date_to,
        },
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setStatsLoading(false);
    }
  }, [filters.site_id, filters.date_from, filters.date_to]);

  // Load conversations
  const loadConversations = useCallback(async () => {
    setConversationsLoading(true);
    try {
      const response = await api.get('/api/intelligence/oversight/conversations', {
        params: filters,
      });
      setConversations(response.data.conversations || []);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setConversationsLoading(false);
    }
  }, [filters]);

  // Load categories
  const loadCategories = useCallback(async () => {
    try {
      const response = await api.get('/api/intelligence/oversight/categories');
      setCategories(response.data || []);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  }, []);

  // Load sites for filter dropdown
  const loadSites = useCallback(async () => {
    try {
      const response = await api.get('/api/sites');
      setSites(response.data || []);
    } catch (error) {
      console.error('Error loading sites:', error);
    }
  }, []);

  // Load protocols for filter dropdown
  const loadProtocols = useCallback(async () => {
    try {
      const response = await api.get('/api/intelligence/protocols', {
        params: { all: true },
      });
      setProtocols(response.data || []);
    } catch (error) {
      console.error('Error loading protocols:', error);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadCategories();
    loadSites();
    loadProtocols();
  }, [loadCategories, loadSites, loadProtocols]);

  // Load data when filters change
  useEffect(() => {
    loadStats();
    loadConversations();
  }, [loadStats, loadConversations]);

  // Load selected conversation detail
  useEffect(() => {
    if (!selectedConversationId) {
      setSelectedConversation(null);
      return;
    }

    const loadConversation = async () => {
      setPanelLoading(true);
      try {
        const response = await api.get(
          `/api/intelligence/oversight/conversations/${selectedConversationId}`
        );
        setSelectedConversation(response.data);
      } catch (error) {
        console.error('Error loading conversation:', error);
        setSelectedConversation(null);
      } finally {
        setPanelLoading(false);
      }
    };

    loadConversation();
  }, [selectedConversationId]);

  // Handle filter change
  const handleFilterChange = (newFilters: OversightFiltersType) => {
    setFilters(newFilters);
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  };

  // Handle row click
  const handleRowClick = (conversationId: string) => {
    setSelectedConversationId(conversationId);
  };

  // Handle save review
  const handleSaveReview = async (conversationId: string, review: UpdateReviewRequest) => {
    setSaving(true);
    try {
      await api.patch(
        `/api/intelligence/oversight/conversations/${conversationId}/review`,
        review
      );
      // Reload conversation and list
      loadConversations();
      loadStats();
      // Update selected conversation
      if (selectedConversationId === conversationId) {
        const response = await api.get(
          `/api/intelligence/oversight/conversations/${conversationId}`
        );
        setSelectedConversation(response.data);
      }
    } catch (error) {
      console.error('Error saving review:', error);
    } finally {
      setSaving(false);
    }
  };

  // Handle dismiss alert
  const handleDismissAlert = async (alertId: string) => {
    try {
      await api.patch(`/api/intelligence/oversight/alerts/${alertId}/dismiss`);
      // Reload conversation
      if (selectedConversationId) {
        const response = await api.get(
          `/api/intelligence/oversight/conversations/${selectedConversationId}`
        );
        setSelectedConversation(response.data);
      }
      loadConversations();
      loadStats();
    } catch (error) {
      console.error('Error dismissing alert:', error);
    }
  };

  // Handle export
  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await api.get('/api/intelligence/oversight/export', {
        params: filters,
        responseType: 'blob',
      });
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `chat-oversight-export-${new Date().toISOString().split('T')[0]}.csv`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting data:', error);
    } finally {
      setExporting(false);
    }
  };

  // Close panel
  const handleClosePanel = () => {
    setSelectedConversationId(null);
  };

  if (user?.role !== 'super_admin') {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Shield size={48} className="mx-auto text-gray-300 mb-4 dark:text-gray-600" />
          <p className="text-gray-500 dark:text-gray-400">Access restricted to super administrators</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <PageHeader
        className="mb-6"
        title="Chat Oversight"
        icon={Shield}
        subtitle="Monitor and review AI conversations for compliance and quality"
        actions={
          <button
            onClick={handleExport}
            disabled={exporting}
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
          >
            {exporting ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Download size={16} />
            )}
            Export CSV
          </button>
        }
      />

      {/* Stats */}
      <div className="mb-6">
        <OversightStats stats={stats} loading={statsLoading} />
      </div>

      {/* Filters */}
      <div className="mb-6">
        <OversightFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          sites={sites}
          protocols={protocols}
        />
      </div>

      {/* Conversations Table */}
      <ConversationTable
        conversations={conversations}
        pagination={pagination}
        loading={conversationsLoading}
        categories={categories}
        onRowClick={handleRowClick}
        onPageChange={handlePageChange}
      />

      {/* Review Panel */}
      {selectedConversationId && (
        <ReviewPanel
          conversation={selectedConversation}
          categories={categories}
          loading={panelLoading}
          saving={saving}
          onClose={handleClosePanel}
          onSave={handleSaveReview}
          onDismissAlert={handleDismissAlert}
        />
      )}
    </div>
  );
}

export default Oversight;
